import { cn } from 'lib/utils';
import { Skeleton } from 'components/ui/skeleton';
import type { TeamsPeopleColumnVisibility } from './teams-view-columns-popover';
import { teamsTableBodyRowClassName } from './teams-styles';

// Same shape as a loaded row, so the table keeps its height while people load.
const SKELETON_ROW_COUNT = 5;
const skeletonKeys = Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => `skeleton-${index}`);

type TeamsPeopleSkeletonProps = {
  columnVisibility: TeamsPeopleColumnVisibility;
  borderColor: string;
};

function NameSkeleton() {
  return (
    <div className="flex items-center gap-3">
      <Skeleton className="size-9 shrink-0 rounded-full" />
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-3 w-44" />
      </div>
    </div>
  );
}

export function TeamsPeopleSkeletonRows({ columnVisibility, borderColor }: TeamsPeopleSkeletonProps) {
  return (
    <>
      {skeletonKeys.map((key) => (
        <tr key={key} className={cn(teamsTableBodyRowClassName, borderColor)} aria-hidden>
          <td className="px-4 py-4 align-middle">
            <Skeleton className="size-4" />
          </td>
          <td className="px-4 py-3.5 align-middle">
            <NameSkeleton />
          </td>
          <td className="px-4 py-3.5 align-middle">
            <Skeleton className="h-9 w-48 max-w-full rounded-card" />
          </td>
          {columnVisibility.creditAccess && (
            <td className="px-4 py-4 align-middle">
              <Skeleton className="h-6 w-10.5 rounded-full" />
            </td>
          )}
          {columnVisibility.lastLogin && (
            <td className="px-4 py-4 align-middle">
              <Skeleton className="h-3.5 w-20" />
            </td>
          )}
          {columnVisibility.joinedOn && (
            <td className="px-4 py-4 align-middle">
              <Skeleton className="h-3.5 w-20" />
            </td>
          )}
          {columnVisibility.status && (
            <td className="px-4 py-4 align-middle">
              <Skeleton className="h-5 w-14 rounded-full" />
            </td>
          )}
          <td className="px-4 py-4 align-middle">
            <Skeleton className="size-4" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function TeamsPeopleSkeletonCards({ columnVisibility, borderColor }: TeamsPeopleSkeletonProps) {
  return (
    <>
      {skeletonKeys.map((key) => (
        <li
          key={key}
          className={cn('rounded-section border bg-true-white p-4 dark:bg-card', borderColor)}
          aria-hidden
        >
          <div className="mb-3 flex items-start gap-2">
            <Skeleton className="mt-2 size-4" />
            <NameSkeleton />
          </div>
          <div className="flex flex-col gap-3 pl-8">
            <Skeleton className="h-9 w-48 max-w-full rounded-card" />
            {columnVisibility.creditAccess && <Skeleton className="h-6 w-10.5 rounded-full" />}
            {columnVisibility.lastLogin && <Skeleton className="h-3.5 w-full" />}
            {columnVisibility.joinedOn && <Skeleton className="h-3.5 w-full" />}
            {columnVisibility.status && <Skeleton className="h-5 w-14 rounded-full" />}
          </div>
        </li>
      ))}
    </>
  );
}
